<template>
    <div class="price-card">
        <div>
            <h5 class="title">
                <img :src="icons[product.type]" alt="" style="height: 35px" />
            </h5>
            <product-details
                :upsellCardType="upsellCardType"
                :product="product"
                :currency-is-changing="currencyIsChanging"
                :show-terms-and-conditions="showTermsAndConditions"
                :vehicle="vehicle"
                :experiment="experiment"
            ></product-details>
        </div>
        <div>
            <p class="price-card-total">
                <gm-tooltip :text="labels.loyaltyAppliedInsteadOfPromo" class="d-inline-block" click="enabled" v-if="loyaltyHigherThanPromo">
                    <i class="fa fa-info-circle text-brand"></i>
                </gm-tooltip>
                <strong class="original-amount" v-if="product.original_price && !currencyIsChanging">{{ originalPrice(product) }}</strong>
                <strong class="amount" v-if="currencyIsChanging">&hellip;</strong>
                <strong class="amount" v-else>{{ vehiclePrice }}</strong>
                <strong>{{ labels.extraPerDay }}</strong>
            </p>
            <div class="desktop-choose-product" v-if="product.is_available">
                <div v-if="keylessLoginRequired && !user && vehicle.is_drive_and_go" class="choose-product col-12" :class="product.type" @click="handleLogin()">
                    {{ $t('bookings.login_register') }}
                    <img :src="brandingImagesPath('/images/large-arrow-white.svg')" alt="right arrow white" v-if="product.type === 'PMP'" />
                    <img :src="brandingImagesPath('/images/large-arrow-green.svg')" alt="right arrow green" v-else />
                </div>
                <div
                    v-if="(!keylessLoginRequired && product.is_available) || (user && vehicle.is_drive_and_go && product.is_available) || (!vehicle.is_drive_and_go && product.is_available)"
                    class="choose-product"
                    @click="selectVehicle(vehicle, product.id)"
                >
                    <button class="btn btn-link-outline text-capitalize btn-main p-3" v-if="vehicle.external_id === vehicleExternalId && selectedProductType === product.type" @click="returnToBooking">
                        {{ $t('PriceCard.current_choice') }}
                    </button>

                    <button v-else-if="product.type === 'BAS'" class="btn btn-link-outline text-capitalize btn-main p-3">
                        {{ bookNowLabel }}
                    </button>

                    <button v-else-if="product.type === 'PMP'" class="btn btn-primary text-capitalize btn-pmp p-3">
                        {{ bookNowLabel }}
                    </button>

                    <button v-else-if="isFullModification" class="btn btn-primary text-capitalize btn-pmp p-3">
                        {{ bookNowLabel }}
                    </button>
                </div>
            </div>
        </div>

        <gm-button top-right-radius="0" top-left-radius="0" :disabled="true" v-if="!product.is_available">
            {{ $t('PriceCard.minimum_age', { age: product.minimum_age }) }}
        </gm-button>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Button from '../shared/Button';
import ProductDetails from './ProductDetails';
import Accounting from '../../mixins/accounting';
import BookingsLang from '../../lang/bookings';
import PriceCardLocalisations from '../../lang/PriceCard';
import merge from 'lodash/merge';
import branding from '../../mixins/branding';
import Tooltip from '../shared/Tooltip';
import modifications from '../../mixins/modifications';

export default {
    name: 'PriceCard',

    props: [
        'product',
        'vehicle',
        'buy',
        'labels',
        'selectedCurrency',
        'selectVehicle',
        'currencyIsChanging',
        'showTermsAndConditions',
        'user',
        'handleLogin',
        'upsellCardType',
        'vehicleExternalId',
        'selectedProductType',
        'loyaltyHigherThanPromo',
        'experiment',
    ],

    i18n: merge(BookingsLang, PriceCardLocalisations),

    components: {
        'gm-button': Button,
        'gm-tooltip': Tooltip,
        ProductDetails,
    },

    mixins: [Accounting, branding, modifications],

    computed: Object.assign(
        {
            bookNowLabel() {
                return this.labels.bookNow;
            },

            vehiclePrice() {
                let product = this.product;
                let bookingProduct = this.booking?.quote?.data?.selected_vehicle?.data?.product?.data;
                let price = this.lastModification?.vehicle_total || bookingProduct?.total_price_with_discounts || product.total_price_with_discounts;

                // For booking mods, when selecting a cheaper vehicle, we want to show the price of the selected vehicle, because the user won't be refunded, so the vehicle price will be the price of the selected vehicle, not the original price of the booking
                if (product.total_price_with_discounts <= price) {
                    return this.quoteHireDuration > 1 ? this.formatMoney(price / this.quoteHireDuration, this.selectedCurrency.code) : this.formatMoney(price, this.selectedCurrency.code);
                }

                return this.quoteHireDuration > 1 ? this.originalPricePerDay(product, this.quoteHireDuration) : this.formatMoney(product.total_price_with_discounts, this.selectedCurrency.code);
            },
        },
        mapGetters({
            booking: 'getBooking',
            quoteHireDuration: 'quoteHireDuration',
            lastModification: 'getLastModification',
        })
    ),

    watch: {
        'booking.reference': {
            immediate: true,
            handler(bookingId) {
                if (!bookingId) {
                    return;
                }

                const bookingKey = this.booking?.reference || bookingId;

                if (!bookingKey) {
                    return;
                }

                this.$store.dispatch('fetchLastModification', {
                    bookingId: bookingKey,
                });
            },
        },

        quoteHireDuration: function (duration) {
            this.vehiclePricePerDay = this.originalPricePerDay(this.product, duration);
        },
    },

    data() {
        return {
            icons: {
                BAS: this.brandingImagesPath('/images/vehicle-listing/basic-badge.svg'),
                PLU: this.brandingImagesPath('/images/vehicle-listing/plus-badge.svg'),
                PRE: this.brandingImagesPath('/images/vehicle-listing/premium-badge.svg'),
                PMP: this.brandingImagesPath('/images/vehicle-listing/premium-plus-badge.svg'),
            },
            productLabels: {
                BAS: 'basic',
                PLU: 'plus',
                PRE: 'premium',
                PMP: 'premium plus+',
            },
            keylessLoginRequired: KEYLESS_LOGIN_REQUIRED !== 'false',
            vehiclePricePerDay: 0,
        };
    },

    methods: {
        returnToBooking() {
            this.$router.push({
                name: 'edit-booking',
                params: { booking: this.$route.params.booking },
            });
        },
    },
};
</script>

<style lang="scss" src="./scss/price-card.scss"></style>
